import { getToken } from "firebase/app-check";
import { appCheck } from "@/firebase";

const RECOMMEND_URL =
  (import.meta as any).env?.VITE_AI_RECOMMEND_URL ||
  "https://recommendwine-r2tye63zjq-an.a.run.app";

const CHARACTERISTICS_URL =
  (import.meta as any).env?.VITE_AI_CHARACTERISTICS_URL ||
  "https://asia-northeast1-wine-sommelier-v2.cloudfunctions.net/wineCharacteristics";

const EXTRACT_URL =
  (import.meta as any).env?.VITE_AI_EXTRACT_URL ||
  "https://asia-northeast1-wine-sommelier-v2.cloudfunctions.net/extractWineFromImage";

async function getAppCheckHeader(): Promise<Record<string, string>> {
  try {
    if (!appCheck) return {};
    const { token } = await getToken(appCheck, /* forceRefresh */ false);
    return token ? { "X-Firebase-AppCheck": token } : {};
  } catch (err) {
    console.warn("App Check token unavailable:", err);
    return {};
  }
}

async function postJson<T>(
  url: string,
  body: unknown,
  opts?: { timeoutMs?: number }
): Promise<T> {
  const appCheckHeaders = await getAppCheckHeader();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts?.timeoutMs ?? 60_000);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-request-id": `req-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        ...appCheckHeaders,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const text = await response.text();
    let data: any;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON from backend (${response.status}): ${text.slice(0, 200)}`);
    }

    if (!response.ok || data?.ok === false) {
      const code = data?.error?.code || response.status;
      const rid = data?.error?.rid || data?.rid || "n/a";
      throw new Error(`Backend error (${code}) / rid=${rid}`);
    }

    return data as T;
  } finally {
    clearTimeout(timeout);
  }
}

export async function backendRecommend(wines: unknown[], userConditions: string) {
  return postJson<{ ok: true; list: any[]; rid: string }>(RECOMMEND_URL, {
    wines,
    userConditions,
  });
}

export async function backendCharacteristics(wine: {
  id: string | number;
  name: string;
  origin: string;
  variety: string;
  type?: string;
  taste?: string;
}) {
  return postJson<{ ok: true; characteristics: any[]; rid: string }>(
    CHARACTERISTICS_URL,
    { wine }
  );
}

export async function backendExtractWineFromImage(imageBase64: string) {
  return postJson<{ ok: true; wine: any; rid: string }>(EXTRACT_URL, {
    imageBase64,
  });
}
